import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SuperadminLayout from '../../components/SuperadminLayout';
import { listCompanies, updateCompany } from '../../api/superadmin';
import { impersonateCompany } from '../../api/auth';
import './SuperadminCompaniesPage.css';

const SuperadminCompaniesPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listCompanies()
      .then((data) => { if (!cancelled) setCompanies(data); })
      .catch(() => { if (!cancelled) setError('Impossible de charger les companies.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggleBlocked = async (company) => {
    setBusyId(company.id);
    setError('');
    try {
      const updated = await updateCompany(company.id, { is_blocked: !company.is_blocked });
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? updated : c)));
    } catch (err) {
      setError('Action impossible sur cette company.');
    } finally {
      setBusyId(null);
    }
  };

  const resetPassword = async (company) => {
    const pwd = window.prompt(`Nouveau mot de passe pour « ${company.name} » (min. 6 caractères) :`);
    if (pwd === null) return;
    if (pwd.trim().length < 6) {
      setError('Mot de passe trop court (min. 6 caractères).');
      return;
    }
    setBusyId(company.id);
    setError('');
    try {
      await updateCompany(company.id, { password: pwd });
      window.alert('Mot de passe réinitialisé.');
    } catch (err) {
      setError('Réinitialisation impossible.');
    } finally {
      setBusyId(null);
    }
  };

  const enterCompany = async (company) => {
    setBusyId(company.id);
    setError('');
    try {
      await impersonateCompany(company.id);
      // Rechargement complet pour que l'AuthProvider company reprenne la session.
      window.location.assign('/dashboard');
    } catch (err) {
      setError("Impossible d'entrer dans cette company.");
      setBusyId(null);
    }
  };

  return (
    <SuperadminLayout>
      <div className="sac-header">
        <h2 className="sac-title">Companies</h2>
        <Link to="/superadmin/companies/new" className="sac-new-btn">+ Nouvelle company</Link>
      </div>

      {error && <p className="sac-error">{error}</p>}
      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="sac-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Code</th>
              <th>Nom</th>
              <th>Username</th>
              <th>Bloquee</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => (
              <tr key={company.id}>
                <td data-label="ID">{company.id}</td>
                <td data-label="Code">{company.code}</td>
                <td data-label="Nom" className="sac-name-cell">
                  <Link to={`/superadmin/companies/${company.id}`}>{company.name}</Link>
                </td>
                <td data-label="Username">{company.username}</td>
                <td data-label="Bloquee">
                  <span className={company.is_blocked ? 'sac-status sac-status-blocked' : 'sac-status sac-status-ok'}>
                    {company.is_blocked ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td className="sac-actions-cell">
                  {/* Groupe dans une div : un <td> en display:flex cassait la
                      mise en page du tableau (bordures/hauteurs decalees). */}
                  <div className="sac-actions">
                    <button
                      type="button"
                      className="sac-btn sac-btn-view"
                      disabled={busyId === company.id}
                      onClick={() => enterCompany(company)}
                      title="Entrer dans cette company"
                    >
                      <span aria-hidden="true">👁</span> Voir
                    </button>
                    <button
                      type="button"
                      className="sac-btn sac-btn-pwd"
                      disabled={busyId === company.id}
                      onClick={() => resetPassword(company)}
                      title="Reinitialiser le mot de passe"
                    >
                      <span aria-hidden="true">🔑</span> Réinit. MDP
                    </button>
                    <button
                      type="button"
                      className={`sac-btn ${company.is_blocked ? 'sac-btn-unblock' : 'sac-btn-block'}`}
                      disabled={busyId === company.id}
                      onClick={() => toggleBlocked(company)}
                    >
                      <span aria-hidden="true">{company.is_blocked ? '🔓' : '🔒'}</span>
                      {company.is_blocked ? ' Debloquer' : ' Bloquer'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminCompaniesPage;
